import type { SkillData } from '@sb/feathers-types';
import type { EquipmentItem } from '@sb/integrations/types';
import type { Step as RcStep } from '@sb/remote-control/types';
import { getStepKindInfo } from '@sb/remote-control/util/stepKindInfo';
import type { Space, StepKind } from '@sb/routine-runner';
import { DEFAULT_SPEED_PROFILE } from '@sb/routine-runner/speed-profile/SpeedProfile';

import {
  type Diagnostic,
  type RoutineView,
  type RuleEvaluationContext,
  type ValidationRule,
} from '../../types';
import { ActionRequiredError } from '../../util/errors';
import { createStepRule, errorToDiagnostics } from '../../util/ruleHelpers';
import { getRunnerArgsSchema } from '../../util/runnerSchemas';

function ensureArgsWithArgumentKind(
  stepKind: StepKind,
  args: unknown,
): RcStep.Arguments {
  const isObj =
    args != null && typeof args === 'object' && !Array.isArray(args);

  const hasArgumentKind =
    isObj && (args as Record<string, unknown>)?.argumentKind != null;

  if (isObj && hasArgumentKind) return args as RcStep.Arguments;
  if (isObj)
    return { argumentKind: stepKind, ...(args as object) } as RcStep.Arguments;

  return { argumentKind: stepKind } as RcStep.Arguments;
}

function findStepNode(
  routine: RoutineView,
  stepId: string,
): { id: string; stepKind: StepKind | string; children: string[] } | null {
  const s = routine.steps.find((x) => x.id === stepId);

  return s
    ? { id: s.id, stepKind: s.stepKind, children: s.children ?? [] }
    : null;
}

export function createCanonicalStepRule(opts: {
  id: string;
  stepKind: StepKind | string;
  validate: (args: {
    stepId: string;
    stepKind: StepKind | string;
    parents: Array<{ id: string; stepKind: StepKind | string }>;
    children: Array<{ id: string; stepKind: StepKind | string }>;
    args: RcStep.Arguments;
    routine: RoutineView;
    ctx: RuleEvaluationContext;
  }) => void;
}): (args: {
  routine: RoutineView;
  ctx: RuleEvaluationContext;
  stepId: string;
}) => Diagnostic[] {
  return ({ routine, ctx, stepId }) => {
    const node = findStepNode(routine, stepId);
    if (!node) return [];

    type RoutineViewWithDerived = RoutineView & {
      derived?: {
        parentsById?: Record<
          string,
          Array<{ id: string; stepKind: StepKind | string }>
        >;
      };
    };

    const parents =
      (routine as RoutineViewWithDerived).derived?.parentsById?.[stepId] ?? [];

    const children =
      (node.children ?? [])
        .map((cid) => findStepNode(routine, cid))
        .filter((x): x is NonNullable<typeof x> => x != null)
        .map((c) => ({ id: c.id, stepKind: c.stepKind })) ?? [];

    const stepCfg = routine.stepConfigurations[stepId];

    const ensuredArgs = ensureArgsWithArgumentKind(
      node.stepKind as StepKind,
      stepCfg?.args as unknown,
    );

    try {
      opts.validate({
        stepId,
        stepKind: node.stepKind,
        parents,
        children,
        args: ensuredArgs,
        routine,
        ctx,
      });
    } catch (err) {
      return errorToDiagnostics(err, stepId, opts.id);
    }

    return [];
  };
}

export function buildStepRule({
  id,
  stepKind,
  runner,
}: {
  id: string;
  stepKind: string;
  runner: (args: {
    routine: RoutineView;
    ctx: RuleEvaluationContext;
    stepId: string;
  }) => Diagnostic[];
}) {
  return createStepRule({
    id,
    stepKinds: [stepKind],
    run: runner,
  });
}

export type CanonicalValidateArgs = {
  stepId: string;
  stepKind: StepKind | string;
  parents: Array<{ id: string; stepKind: StepKind | string }>;
  children: Array<{ id: string; stepKind: StepKind | string }>;
  args: RcStep.Arguments;
  routine: RoutineView;
  ctx: RuleEvaluationContext;
};

export abstract class CanonicalStepRuleBase {
  public readonly id: string;

  public readonly stepKind: string;

  protected constructor(id: string, stepKind: string) {
    this.id = id;
    this.stepKind = stepKind;
  }

  /**
   * Ensure the provided args match this.stepKind.
   * Throws TypeError if the argument kind does not match.
   *
   * @throws {TypeError} When args.argumentKind !== this.stepKind
   */
  protected ensureArgumentKind(
    args: { argumentKind?: string } | unknown,
  ): void {
    const argKind = (args as { argumentKind?: string } | undefined)
      ?.argumentKind;

    if (argKind !== this.stepKind) {
      throw new TypeError(`Expected argument kind ${this.stepKind}`);
    }
  }

  /**
   * Combine global and routine space items into a single array.
   *
   * @returns The combined space array.
   */
  protected getSpaceItems(
    routine: { space?: Space.Item[] } | undefined,
    ctx: { globalSpace?: Space.Item[] } | undefined,
  ): Space.Item[] {
    return [
      ...((ctx?.globalSpace ?? []) as Space.Item[]),
      ...((routine?.space ?? []) as Space.Item[]),
    ];
  }

  /**
   * Find equipment by id in the validation context.
   *
   * @returns {any|undefined} The equipment config if found
   */
  protected findEquipment(
    ctx: { equipment?: EquipmentItem[] } | undefined,
    id: string,
  ): EquipmentItem | undefined {
    return (ctx?.equipment ?? []).find((e) => e.id === id);
  }

  /** Find a persisted skill by id in the validation context. */
  protected findSkill(
    ctx: { skills?: SkillData[] } | undefined,
    id: string,
  ): SkillData | undefined {
    return (ctx?.skills ?? []).find((s) => s.id === id);
  }

  // Cross-check that the step's storage/UI args produce something that
  // survives the routine runner's Zod schema. The conversion step is what
  // makes this safe to run on UI shape — fields are renamed in runner shape
  // (e.g. SetIO outputs → changes), so we convert via the step's
  // `toRoutineRunner` before the `safeParse`. Best-effort: if the conversion
  // throws, fall back to validating the UI args as-is so a broken converter
  // can't mask a genuine config error.
  protected runnerSchemaValidate(args: CanonicalValidateArgs): void {
    const schema = getRunnerArgsSchema(this.stepKind);
    if (!schema) return;

    const originalArgs = (args.args ?? {}) as Record<string, unknown>;
    const { argumentKind: _argumentKind, ...rest } = originalArgs;

    let candidate: Record<string, unknown> = rest;

    try {
      const toRR = getStepKindInfo(this.stepKind as StepKind).toRoutineRunner;

      if (typeof toRR === 'function') {
        const converted = toRR({
          // `toRoutineRunner` only reads stepKind / args; pass minimal stubs
          // for the other fields it requires by type.
          stepConfiguration: {
            id: args.stepId,
            stepKind: this.stepKind as StepKind,
            args: originalArgs as unknown,
          } as any,
          stepData: { id: args.stepId } as any,
          equipment: args.ctx?.equipment ?? [],
          baseSpeedProfile: DEFAULT_SPEED_PROFILE,
          codeBlocks: [],
        });

        const convertedArgs = (converted as { args?: unknown })?.args;

        if (convertedArgs && typeof convertedArgs === 'object') {
          candidate = convertedArgs as Record<string, unknown>;
        }
      }
    } catch {
      // Best-effort conversion; fall back to UI args if conversion fails.
    }

    // Validate only the keys present (treat missing keys as ok); prefer deep partial when available.
    const schemaAny = schema as unknown as {
      deepPartial?: () => typeof schema;
      partial?: () => typeof schema;
    };

    const partialSchema =
      schemaAny.deepPartial?.() ?? schemaAny.partial?.() ?? schema;

    const result = partialSchema.safeParse(candidate);
    if (result.success) return;

    const issue = result.error.issues[0];

    const fieldId =
      Array.isArray(issue?.path) && issue.path.length > 0
        ? String(issue.path[0])
        : undefined;

    throw new ActionRequiredError({
      kind: 'invalidConfiguration',
      message: issue?.message || 'Arguments are not valid for this step',
      fieldId,
    });
  }

  protected abstract validate(args: CanonicalValidateArgs): void;

  public toValidationRule(): ValidationRule {
    const runner = createCanonicalStepRule({
      id: this.id,
      stepKind: this.stepKind,
      validate: (args) => {
        // Validations occur in priority order:
        // 1) Ensure arguments are for the expected step kind
        this.ensureArgumentKind(args.args);
        // 2) Run subclass-provided validations
        this.validate(args);
        // 3) Validate against the runner schema
        this.runnerSchemaValidate(args);
      },
    });

    return buildStepRule({
      id: this.id,
      stepKind: this.stepKind,
      runner,
    });
  }
}
